import { Analytics } from '@cobliteam/analytics';
import { fromE164BR } from '@cobliteam/api-lib';
import type { SessionUser } from '@cobliteam/common-types';
import { Location } from '@cobliteam/common-types';
import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import useBasicValueMaturity from '@cobliteam/hooks/src/useBasicValueMaturity/useBasicValueMaturity';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import useIsOnboardingFleet from '@cobliteam/hooks/src/useIsOnboardingFleet/useIsOnboardingFleet';
import useUserAvatar from '@cobliteam/hooks/src/useSession/useUserAvatar';
import { useTranslation } from '@cobliteam/i18n';
import { BASIC_VALUE_STEP_PERMISSIONS, usePermissions } from '@cobliteam/permissions';
import Avatar, { AvatarFallback, AvatarImage } from '@cobliteam/ui/components/Avatar';
import Button from '@cobliteam/ui/components/Button';
import DropdownMenu from '@cobliteam/ui/components/DropdownMenu';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Icon from '@cobliteam/ui/icons';
import { useHistory } from 'react-router-dom';

interface AppUserProps {
  user: SessionUser;
  logout: () => void;
}

export default function AppUser({ user, logout }: AppUserProps) {
  const { t } = useTranslation('app-header');
  const history = useHistory();
  const { runtimeFlags } = useDashboard();
  const featureFlags = useFeatureFlags();
  // O item de menu do "Guia de configuração" depende só de permissão (OR das permissões das etapas —
  // AE-2446): a página é acessível a qualquer frota, com ou sem 90 dias de contrato. A janela de
  // onboarding decide qual home aparece e se o "!" de pendência é exibido, não se o acesso existe.
  const { isOnboardingFleet } = useIsOnboardingFleet();
  const { isAccessAllowed } = usePermissions();
  const hasBasicValueAccess = isAccessAllowed(BASIC_VALUE_STEP_PERMISSIONS);
  const { isComplete, isLoading: isMaturityLoading } = useBasicValueMaturity();
  // Foto de perfil enviada em /config/account. Mesma query key do `UpdateUserAvatar`, então
  // um upload/remoção lá reflete aqui na hora, sem reload.
  const { avatarUrl } = useUserAvatar();
  // Pending "!" highlight on the avatar while the fleet is in onboarding and setup is incomplete.
  // Also mirrored on the "Guia de configuração" menu item so the user knows where the "!" points.
  const basicValuePending = !(isMaturityLoading || isComplete) && isOnboardingFleet && hasBasicValueAccess;

  const goToClientArea = () => {
    Analytics.track('UserButton - Navigate - ClientArea');
    history.push(Location.BUSINESS_MANAGER);
  };

  const goToFinance = () => {
    Analytics.track('UserButton - Navigate - Finance');
    history.push(Location.BUSINESS_MANAGER_BOLETOS);
  };

  const handleLogout = () => {
    Analytics.track('UserButton - Click - Logout');
    logout();
  };

  const goToMyAccount = () => {
    Analytics.track('UserButton - Navigate - MyAccount');
    history.push(Location.CONFIG_ACCOUNT);
  };

  const goToBasicValue = () => {
    Analytics.track('UserButton - Navigate - BasicValue');
    history.push(Location.BASIC_VALUE);
  };

  const goToHelpCenter = () => {
    Analytics.track('NavBar - HelpCenter - Click');
    window.open('https://suporte.cobli.co/', '_blank', 'noopener,noreferrer');
  };

  const goToTrainings = () => {
    Analytics.track('UserButton - Navigate - Trainings');
    history.push(Location.TRAINING);
  };

  const goToSettings = () => {
    Analytics.track('UserButton - Navigate - Settings');
    history.push(Location.CONFIG_FLEET);
  };

  const goToContact = () => {
    Analytics.track('UserButton - Navigate - Contact');
    window.open('https://suporte.cobli.co/contato-cobli', '_blank');
  };

  const goToCRM = () => {
    Analytics.track('UserButton - Navigate - Orders');
    history.push(Location.ORDERS);
  };

  const avatarTrigger = (
    <Button
      variant='surface'
      color='default'
      size='sm'
      data-testid='app-user'
      aria-label={basicValuePending ? t('user.menuAriaPending') : t('user.menuAria')}
      className='relative size-8 overflow-hidden rounded-full p-0'
    >
      {/* Radix Avatar só troca para a imagem depois que ela carrega, então o ícone segue sendo
          o estado padrão (sem foto, carregando, ou URL quebrada). */}
      <Avatar className='size-8 rounded-full'>
        {avatarUrl && <AvatarImage src={avatarUrl} alt='' data-testid='app-user-avatar-image' />}
        <AvatarFallback>
          <Icon name='user' size='md' />
        </AvatarFallback>
      </Avatar>
    </Button>
  );

  return (
    <DropdownMenu
      onOpenChange={(isOpen) => {
        if (isOpen) {
          Analytics.track('UserButton - Open');
        }
      }}
    >
      <Tooltip content={t('user.myAccount')} asChild>
        <DropdownMenu.Trigger asChild>
          <div className='relative'>
            {avatarTrigger}
            {basicValuePending && (
              <span
                aria-hidden
                data-testid='app-user-pending-badge'
                className='pointer-events-none absolute -right-0.5 -bottom-0.5 flex size-3.5 items-center justify-center rounded-full bg-warning-9 font-bold text-[9px] text-white leading-none ring-2 ring-white'
              >
                !
              </span>
            )}
          </div>
        </DropdownMenu.Trigger>
      </Tooltip>

      <DropdownMenu.Content className='max-w-64'>
        <DropdownMenu.Label className='flex min-w-0 flex-col items-start gap-0 px-2 pt-1'>
          <span className='truncate font-medium text-gray-12 text-sm'>{user.name}</span>
          <span className='truncate font-normal text-gray-10 text-xs'>{user.email}</span>
          {user.phoneNumber && <span className='truncate font-normal text-gray-10 text-xs'>{fromE164BR(user.phoneNumber)}</span>}
        </DropdownMenu.Label>
        <DropdownMenu.Separator />
        <DropdownMenu.Group>
          {/* "Minha conta" fica fora do gate de frota: sem grupo/veículo válido o usuário ainda
              precisa acessar os próprios dados (nome, telefone, senha) para se auto-resolver. */}
          <DropdownMenu.Item onClick={goToMyAccount}>
            <DropdownMenu.Icon icon={<Icon name='user' />} />
            <DropdownMenu.Label>{t('user.myAccount')}</DropdownMenu.Label>
          </DropdownMenu.Item>
          {!runtimeFlags.noValidGroupOrVehicles && (
            <>
              {hasBasicValueAccess && (
                <DropdownMenu.Item onClick={goToBasicValue}>
                  <DropdownMenu.Icon icon={<Icon name='flag' />} />
                  <DropdownMenu.Label>{t('user.basicValue')}</DropdownMenu.Label>
                  {basicValuePending && (
                    <span aria-hidden className='ml-auto flex size-4 shrink-0 items-center justify-center rounded-full bg-warning-9 font-bold text-[10px] text-white leading-none'>
                      !
                    </span>
                  )}
                </DropdownMenu.Item>
              )}
              <DropdownMenu.Item onClick={goToSettings}>
                <DropdownMenu.Icon icon={<Icon name='settings' />} />
                <DropdownMenu.Label>{t('user.settings')}</DropdownMenu.Label>
              </DropdownMenu.Item>
            </>
          )}
        </DropdownMenu.Group>
        <DropdownMenu.Separator />
        {/* Ordem dos grupos segue o Figma (AE-2495): conta → área do cliente → apoio → sair. */}
        {!runtimeFlags.noValidGroupOrVehicles && (
          <>
            <DropdownMenu.Group>
              {featureFlags.businessManager && (
                <DropdownMenu.Item onClick={goToClientArea}>
                  <DropdownMenu.Icon icon={<Icon name='building' />} />
                  <DropdownMenu.Label>{t('user.clientArea')}</DropdownMenu.Label>
                </DropdownMenu.Item>
              )}
              {featureFlags.businessManager && (
                <DropdownMenu.Item onClick={goToFinance}>
                  <DropdownMenu.Icon icon={<Icon name='dollar-sign' />} />
                  <DropdownMenu.Label>{t('user.finance')}</DropdownMenu.Label>
                </DropdownMenu.Item>
              )}
              <DropdownMenu.Item onClick={goToCRM}>
                <DropdownMenu.Icon icon={<Icon name='shopping-cart' />} />
                <DropdownMenu.Label>{t('user.orders')}</DropdownMenu.Label>
              </DropdownMenu.Item>
            </DropdownMenu.Group>
            <DropdownMenu.Separator />
          </>
        )}
        <DropdownMenu.Group>
          <DropdownMenu.Item onClick={goToTrainings}>
            <DropdownMenu.Icon icon={<Icon name='graduation-cap' />} />
            <DropdownMenu.Label>{t('user.trainings')}</DropdownMenu.Label>
          </DropdownMenu.Item>

          <DropdownMenu.Item onClick={goToHelpCenter}>
            <DropdownMenu.Icon icon={<Icon name='circle-help' />} />
            <DropdownMenu.Label>{t('help.helpCenter')}</DropdownMenu.Label>
          </DropdownMenu.Item>

          <DropdownMenu.Item onClick={goToContact}>
            <DropdownMenu.Icon icon={<Icon name='headset' />} />
            <DropdownMenu.Label>{t('user.contact')}</DropdownMenu.Label>
          </DropdownMenu.Item>
        </DropdownMenu.Group>
        <DropdownMenu.Separator />

        <DropdownMenu.Item onClick={handleLogout}>
          <DropdownMenu.Icon icon={<Icon name='log-out' />} />
          <DropdownMenu.Label>{t('user.logout')}</DropdownMenu.Label>
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
}
